import React, { useState, useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { LineChart, FileText, DollarSign, Wallet, CreditCard } from 'lucide-react';
import { obtenerHistorialPagos, obtenerEgresos } from '../../services/FinanzasService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
    });
};

const Dashboard = () => {
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(new Date());
  const [ingresosFiltrados, setIngresosFiltrados] = useState([]);
  const [egresosFiltrados, setEgresosFiltrados] = useState([]);

  // Función para filtrar datos por el mes y año seleccionados
  const filtrarDatosPorPeriodo = (ingresos, egresos, fecha) => {
    const mes = fecha.getMonth() + 1; // getMonth() devuelve 0-11, sumamos 1 para obtener 1-12
    const anio = fecha.getFullYear();
    
    // Filtrar ingresos
    const ingresosFiltrados = ingresos.filter(ingreso => {
      if (ingreso.fecha_pago) {
        const fechaIngreso = new Date(ingreso.fecha_pago);
        return fechaIngreso.getMonth() + 1 === mes && fechaIngreso.getFullYear() === anio;
      } else if (ingreso.mes_pagado && ingreso.anio_pagado) {
        return parseInt(ingreso.mes_pagado) === mes && parseInt(ingreso.anio_pagado) === anio;
      }
      return false;
    });
    
    // Filtrar egresos
    const egresosFiltrados = egresos.filter(egreso => {
      if (egreso.fecha) {
        const fechaEgreso = new Date(egreso.fecha);
        return fechaEgreso.getMonth() + 1 === mes && fechaEgreso.getFullYear() === anio;
      }
      return false;
    });
    
    return { ingresosFiltrados, egresosFiltrados };
  };

  // Efecto para cargar los datos
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [ingresosRes, egresosRes] = await Promise.all([
          obtenerHistorialPagos(),
          obtenerEgresos()
        ]);
        const datos = ingresosRes.historialPagos || [];
        const gastos = egresosRes.egresos || [];
        
        setIngresos(datos);
        setEgresos(gastos);
        
        // Filtrar datos por el mes actual
        const { ingresosFiltrados, egresosFiltrados } = filtrarDatosPorPeriodo(datos, gastos, periodo);
        setIngresosFiltrados(ingresosFiltrados);
        setEgresosFiltrados(egresosFiltrados);
      } catch (error) {
        showErrorToast('Error al obtener datos de la base de datos');
      } finally {
        setLoading(false);
      }
    };
    cargarDatos();
  }, []);
  
  // Efecto para actualizar los datos filtrados cuando cambia el periodo
  useEffect(() => {
    if (ingresos.length > 0 || egresos.length > 0) {
      const { ingresosFiltrados, egresosFiltrados } = filtrarDatosPorPeriodo(ingresos, egresos, periodo);
      setIngresosFiltrados(ingresosFiltrados);
      setEgresosFiltrados(egresosFiltrados);
    }
  }, [periodo, ingresos, egresos]);

  const calcularTotalIngresos = () => {
    return ingresosFiltrados.reduce((total, ingreso) => total + parseFloat(ingreso.monto_pagado), 0);
  };

  const calcularTotalEgresos = () => {
    return egresosFiltrados.reduce((total, egreso) => total + parseFloat(egreso.monto), 0);
  };

  // Función para formatear montos con separador de miles
  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(monto);
  };

  // Obtener el mes actual en formato texto
  const nombreMes = periodo.toLocaleString('es-ES', { month: 'long' });
  const año = periodo.getFullYear();
  
  // Calcular el balance total
  const totalIngresos = calcularTotalIngresos();
  const totalEgresos = calcularTotalEgresos();
  const balance = totalIngresos - totalEgresos;
  
  // Determinar el color del balance
  const balanceColor = balance >= 0 ? 'text-emerald-600' : 'text-rose-600';
  const balanceIndicator = balance >= 0 ? '+' : '-';

  return (
    <div className="p-2 sm:p-4">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="mt-16"
        style={{ zIndex: 9999 }}
      />
      
      {/* Cabecera con periodo */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
        <h2 className="text-lg font-medium text-gray-800 mb-2 sm:mb-0">
          Resumen Financiero
        </h2>
        <div className="flex items-center space-x-2">
          <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm">
            {nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1)} {año}
          </div>
          <button 
            onClick={() => setPeriodo(new Date())} 
            className="text-xs text-gray-600 hover:text-gray-800 transition-colors"
            title="Volver al mes actual">
            Mes actual
          </button>
        </div>
      </div>
      
      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-3 rounded shadow-sm border-l-2 border border-emerald-400 border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-gray-700 font-medium text-sm">INGRESOS</h3>
            <Wallet className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="text-lg font-medium text-gray-800">
            ${formatearMonto(totalIngresos)}
          </p>
        </div>
        
        <div className="bg-white p-3 rounded shadow-sm border-l-2 border border-rose-400 border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-gray-700 font-medium text-sm">EGRESOS</h3>
            <CreditCard className="h-4 w-4 text-rose-500" />
          </div>
          <p className="text-lg font-medium text-gray-800">
            ${formatearMonto(totalEgresos)}
          </p>
        </div>
        
        <div className="bg-white p-3 rounded shadow-sm border-l-2 border border-blue-400 border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-gray-700 font-medium text-sm">BALANCE</h3>
            <LineChart className="h-4 w-4 text-blue-500" />
          </div>
          <div className="flex items-center">
            <p className={`text-lg font-medium ${balanceColor}`}>
              {balanceIndicator}${formatearMonto(Math.abs(balance))}
            </p>
          </div>
        </div>
      </div>

      {/* Distribución de ingresos y gastos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Distribución de ingresos */}
        <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200">
            <h3 className="font-medium text-gray-700 text-sm">Distribución de Ingresos</h3>
          </div>
          
          {ingresosFiltrados.length > 0 ? (
            <div className="p-4">
              {Object.entries(ingresosFiltrados.reduce((acc, ingreso) => {
                // Intentamos categorizar por tipo_pago primero
                let categoria;
                if (ingreso.tipo_pago) {
                  categoria = ingreso.tipo_pago;
                } else if (ingreso.concepto) {
                  categoria = ingreso.concepto;
                } else if (ingreso.nombre_cliente) {
                  categoria = `Cliente: ${ingreso.nombre_cliente}`;
                } else {
                  categoria = 'Otros ingresos';
                }
                acc[categoria] = (acc[categoria] || 0) + parseFloat(ingreso.monto_pagado);
                return acc;
              }, {})).sort((a, b) => b[1] - a[1]).map(([categoria, monto], index) => {
                const porcentaje = ((monto / calcularTotalIngresos()) * 100).toFixed(1);
                // Colores para las barras de ingresos
                const barColors = [
                  'bg-emerald-500', 'bg-green-500', 'bg-teal-500', 'bg-cyan-500', 'bg-sky-500',
                  'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500'
                ];
                const colorIndex = index % barColors.length;
                
                return (
                  <div key={categoria} className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">{categoria}</span>
                      <div className="flex items-center">
                        <span className="text-sm font-medium mr-2">${formatearMonto(monto)}</span>
                        <span className="text-xs text-gray-500">{porcentaje}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${barColors[colorIndex]}`} 
                        style={{ width: `${porcentaje}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500 text-sm">
              No hay datos de ingresos disponibles
            </div>
          )}
        </div>
        
        {/* Distribución de gastos */}
        <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200">
            <h3 className="font-medium text-gray-700 text-sm">Distribución de Gastos</h3>
          </div>
          
          {egresosFiltrados.length > 0 ? (
            <div className="p-4">
              {Object.entries(egresosFiltrados.reduce((acc, egreso) => {
                acc[egreso.tipo_egreso] = (acc[egreso.tipo_egreso] || 0) + parseFloat(egreso.monto);
                return acc;
              }, {})).sort((a, b) => b[1] - a[1]).map(([tipo, monto], index) => {
                const porcentaje = ((monto / calcularTotalEgresos()) * 100).toFixed(1);
                // Colores para las barras de egresos
                const barColors = [
                  'bg-rose-500', 'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
                  'bg-pink-500', 'bg-fuchsia-500', 'bg-purple-500', 'bg-violet-500'
                ];
                const colorIndex = index % barColors.length;
                
                return (
                  <div key={tipo} className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">{tipo}</span>
                      <div className="flex items-center">
                        <span className="text-sm font-medium mr-2">${formatearMonto(monto)}</span>
                        <span className="text-xs text-gray-500">{porcentaje}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${barColors[colorIndex]}`} 
                        style={{ width: `${porcentaje}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500 text-sm">
              No hay datos de egresos disponibles
            </div>
          )}
        </div>
      </div>
      

    </div>
  );
};

const FinanzasReportes = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);

  const tabs = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: LineChart,
      path: '',
    },
    {
      id: 'ingresos', 
      name: 'Ingresos',
      icon: Wallet,
      path: '/ingresos',
    },
    {
      id: 'gastos',
      name: 'Gastos', 
      icon: CreditCard,
      path: '/gastos',
    },
    {
      id: 'reportes',
      name: 'Reportes',
      icon: FileText,
      path: '/reportes',
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="border-b border-gray-200">
        <div className="px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 flex items-center gap-2">
            <DollarSign className="h-5 w-5 sm:h-6 sm:w-6" />
            Gestión Financiera
          </h1>
        </div>
        
        <div className="px-2 sm:px-6 overflow-x-auto">
          <nav className="flex space-x-2 sm:space-x-4 pb-1 sm:pb-0 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <Link
                key={tab.id}
                to={`/admin/finanzas${tab.path}`}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 border-b-2 text-xs sm:text-sm font-medium whitespace-nowrap`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                {tab.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      <div className="min-h-[calc(100vh-16rem)]">
        <div className="animate-fadeIn">
          <ToastContainer 
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          {activeTab === 'dashboard' ? <Dashboard /> : <Outlet />}
        </div>
      </div>
    </div>
  );
};

export default FinanzasReportes;
